"use client"

import type { Theme } from "@/models/themes.model"

import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  LogOut,
} from "lucide-react"
import { useTranslations } from "next-intl"
import { useTheme } from "next-themes"
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useRouter } from "@/i18n/navigation"
import { cn } from "@/lib/utils"
import { useAuthStore } from "@/stores/auth-store"
import { useCustomTheme } from "../provider/custom-theme-provider"
import { ThemePalette } from "../themes/theme-switcher"
import { Icon } from "../ui/icon"
import { Switch } from "../ui/switch"

export function NavUser({ collapse }: { collapse: boolean }) {
  const { isMobile } = useSidebar()
  const { themeList, setActiveTheme, activeTheme } = useCustomTheme()
  const { theme } = useTheme()
  const t = useTranslations("navbar")
  const { logout, user } = useAuthStore()
  const router = useRouter()
  async function handleLogout() {
    await logout()
    router.push("/login")
  }

  function handleThemeChange(newTheme: Theme | undefined, variant?: "light" | "dark") {
    console.log("handleThemeChange", newTheme, variant)
    setActiveTheme(newTheme, variant)
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className={cn("data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground", collapse && "p-0")}
              size="lg"
              tooltip={{
                children: user?.name,
                hidden: true,
              }}
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage alt={user?.name} src={user?.avatar} />
                <AvatarFallback className="rounded-lg">{user?.username?.charAt(0)}</AvatarFallback>
              </Avatar>
              {!collapse && (
                <>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user?.name}</span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage alt={user?.name} src={user?.avatar} />
                  <AvatarFallback className="rounded-lg">{user?.username?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user?.name}</span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <BadgeCheck />
                {t("user.account")}
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Bell />
                {t("user.notifications")}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem className="flex items-center justify-between" onClick={() => handleThemeChange(activeTheme, theme === "dark" ? "light" : "dark")}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" icon={theme === "dark" ? "lucide:moon" : "lucide:sun"} />
                  {theme === "dark" ? t("user.dark_mode") : t("user.light_mode")}
                </div>
                <Switch checked={theme === "dark"} />
              </DropdownMenuItem>
              {/* <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Icon className="mr-2 h-4 w-4" icon="lucide:palette" />
                  {t("user.theme")}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {themeList.map(t => (
                    <DropdownMenuItem
                      key={t.id}
                      disabled={t.id === activeTheme?.id}
                      onClick={() => handleThemeChange(t, theme === "dark" ? "dark" : "light")}
                    >
                      <ThemePalette theme={t} />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub> */}
            </DropdownMenuGroup>
            <DropdownMenuItem onClick={() => void handleLogout()}>
              <LogOut />
              {t("user.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
